import { useAtom, useAtomValue } from "jotai";
import {
  DashboardLayoutStateStore,
  SelectedDashboardIdAtom,
} from "../../store/DashboardLayout";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  _deleteLayout,
  _updateLayoutName,
  createDashboardConfiguration,
  getAllLayoutsForUser,
  updateAccount,
} from "../../api";
import { AccountAtom } from "../../store";
import { DashboardConfiguration } from "../../types/DashboardConfiguration";

const buildEmptyLayouts = () => ({
  lg: [],
  md: [],
  sm: [],
  xs: [],
  xxs: [],
});

interface useDashboardSelectorControllerResponse {
  configurations: DashboardConfiguration[];
  selectedConfigurationId: number | undefined;
  setSelectedConfiguration: (id: number) => void;
  selectedDashboard?: string;
  renameDashboard: (dashboardId: number, name: string) => Promise<void>;
  createDashboard: (name: string) => Promise<DashboardConfiguration | null>;
  deleteDashboard: (dashboardId: number) => Promise<void>;
  loading: {
    creating: boolean;
    renaming: boolean;
    deleting: boolean;
    fetching: boolean;
  };
  error: string | null;
  clearError: () => void;
}

export const useDashboardSelectorController =
  (): useDashboardSelectorControllerResponse => {
    const [dashboardLayouts, setDashboardLayouts] = useAtom(
      DashboardLayoutStateStore
    );

    const accountState = useAtomValue(AccountAtom);
    const [selectedDashboardId, setSelectedDashboardId] = useAtom(
      SelectedDashboardIdAtom
    );
    const selectedAccount = accountState.selectedAccount;

    // Loading and error states
    const [loading, setLoading] = useState({
      creating: false,
      renaming: false,
      deleting: false,
      fetching: false,
    });
    const [error, setError] = useState<string | null>(null);

    const clearError = useCallback(() => setError(null), []);

    const selectedDashboard = useMemo(() => {
      const configurations = dashboardLayouts.configurations;
      const defaultDashboardId =
        accountState.selectedAccount?.defaultDashboardConfigurationId;

      if (configurations && configurations.length > 0) {
        const activeDashboardId = selectedDashboardId || defaultDashboardId;
        const config = configurations.find(
          (x) => x.configurationId === activeDashboardId
        );

        if (!config) {
          // Default to the first dashboard if the selected one is not found
          const firstConfig = configurations[0];
          setSelectedDashboardId(firstConfig.configurationId);
          return firstConfig.name;
        }

        return config.name;
      }

      return undefined;
    }, [
      selectedDashboardId,
      accountState.selectedAccount?.defaultDashboardConfigurationId,
      dashboardLayouts.configurations,
    ]);

    const setSelectedConfiguration = useCallback(
      async (id: number | undefined) => {
        if (
          selectedAccount &&
          selectedAccount.defaultDashboardConfigurationId !== id
        ) {
          await updateAccount(selectedAccount.id, {
            ...selectedAccount,
            defaultDashboardConfigurationId: id,
          });
        }

        if (selectedDashboardId !== id) {
          setSelectedDashboardId(id);

          setDashboardLayouts((prev) => {
            const config = prev.configurations.find((x) => {
              return x.configurationId === id;
            });

            return {
              ...prev,
              // layouts: config?.layout,
              selectedConfiguration: config,
            };
          });
        }
      },
      [selectedAccount, selectedDashboardId]
    );

    const fetchLayout = useCallback(async (): Promise<
      DashboardConfiguration[]
    > => {
      if (!accountState.selectedAccount?.id) {
        setDashboardLayouts((prev) => ({
          ...prev,
          layouts: buildEmptyLayouts(),
          selectedConfiguration: undefined,
          configurations: [],
        }));
        return [];
      }

      try {
        setLoading(prev => ({ ...prev, fetching: true }));
        setError(null);
        
        const configurations =
          (await getAllLayoutsForUser()) as DashboardConfiguration[];

        if (configurations.length > 0) {
          const config = configurations[0];
          setDashboardLayouts((prev) => ({
            ...prev,
            layouts: {
              ...buildEmptyLayouts(),
              ...(config?.layout || {}),
            },
            selectedConfiguration: config,
            configurations: configurations.sort((a, b) => {
              return a.name.localeCompare(b.name);
            }),
          }));

          const urlId = new URLSearchParams(window.location.search).get(
            "dashboardId"
          );
          const parsedUrlId = urlId !== null ? Number(urlId) : undefined;
          const layoutId = Number.isFinite(parsedUrlId)
            ? parsedUrlId
            : selectedDashboardId ??
              accountState.selectedAccount?.defaultDashboardConfigurationId;

          setSelectedConfiguration(layoutId);
        } else {
          setDashboardLayouts((prev) => ({
            ...prev,
            layouts: buildEmptyLayouts(),
            selectedConfiguration: undefined,
            configurations: [],
          }));
        }
        return configurations ?? [];
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboards';
        setError(errorMessage);
        console.error('Failed to fetch dashboard layouts:', err);
        setDashboardLayouts((prev) => ({
          ...prev,
          layouts: buildEmptyLayouts(),
          selectedConfiguration: undefined,
          configurations: [],
        }));
        return [];
      } finally {
        setLoading(prev => ({ ...prev, fetching: false }));
      }
    }, [
      accountState.selectedAccount?.defaultDashboardConfigurationId,
      accountState.selectedAccount?.id,
      selectedDashboardId,
      setDashboardLayouts,
      setSelectedConfiguration,
    ]);

    const renameDashboard = useCallback(
      async (dashboardId: number, name: string): Promise<void> => {
        if (!name?.trim()) {
          throw new Error('Dashboard name cannot be empty');
        }

        const trimmedName = name.trim();
        
        // Check for duplicate names (case-insensitive)
        const existingNames = dashboardLayouts.configurations
          .filter(config => config.configurationId !== dashboardId)
          .map(config => config.name.toLowerCase());
        
        if (existingNames.includes(trimmedName.toLowerCase())) {
          throw new Error('A dashboard with this name already exists');
        }

        try {
          setLoading(prev => ({ ...prev, renaming: true }));
          setError(null);
          
          await _updateLayoutName(dashboardId, trimmedName);

          // Optimistic update
          setDashboardLayouts((prev) => {
            const configurations = prev.configurations.map((x) => {
              if (x.configurationId === dashboardId) {
                return { ...x, name: trimmedName };
              }
              return x;
            });

            return {
              ...prev,
              configurations: configurations.sort((a, b) => a.name.localeCompare(b.name)),
            };
          });
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to rename dashboard';
          setError(errorMessage);
          console.error('Failed to rename dashboard:', err);
          throw err;
        } finally {
          setLoading(prev => ({ ...prev, renaming: false }));
        }
      },
      [dashboardLayouts.configurations]
    );

    const createDashboard = useCallback(
      async (name: string): Promise<DashboardConfiguration | null> => {
        if (!name?.trim()) {
          throw new Error('Dashboard name cannot be empty');
        }

        if (!accountState.selectedAccount?.id) {
          throw new Error('No account selected');
        }

        const trimmedName = name.trim();
        
        // Check for duplicate names (case-insensitive)
        const existingNames = dashboardLayouts.configurations.map(config => 
          config.name.toLowerCase()
        );
        
        if (existingNames.includes(trimmedName.toLowerCase())) {
          throw new Error('A dashboard with this name already exists');
        }

        try {
          setLoading(prev => ({ ...prev, creating: true }));
          setError(null);
          
          const result = await createDashboardConfiguration(
            accountState.selectedAccount.id,
            trimmedName
          );

          if (result?.config) {
            // Refresh layouts to get the new dashboard
            await fetchLayout();
            // Immediately select the new dashboard
            setSelectedDashboardId(result.config.configurationId);
            return result.config;
          }
          
          throw new Error('Failed to create dashboard configuration');
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to create dashboard';
          setError(errorMessage);
          console.error('Failed to create dashboard:', err);
          throw err;
        } finally {
          setLoading(prev => ({ ...prev, creating: false }));
        }
      },
      [accountState.selectedAccount?.id, dashboardLayouts.configurations, fetchLayout]
    );

    const deleteDashboard = useCallback(async (dashboardId: number): Promise<void> => {
      if (!dashboardId) {
        throw new Error('Invalid dashboard ID');
      }

      try {
        setLoading(prev => ({ ...prev, deleting: true }));
        setError(null);
        
        await _deleteLayout(dashboardId);
        const configurations = await fetchLayout();
        
        // Select the first available dashboard after deletion
        const id = configurations.length > 0 ? configurations[0].configurationId : undefined;
        setSelectedConfiguration(id);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete dashboard';
        setError(errorMessage);
        console.error('Failed to delete dashboard:', err);
        throw err;
      } finally {
        setLoading(prev => ({ ...prev, deleting: false }));
      }
    }, [fetchLayout, setSelectedConfiguration]);

    useEffect(() => {
      if (
        !selectedDashboardId &&
        accountState.selectedAccount?.defaultDashboardConfigurationId
      ) {
        setSelectedDashboardId(
          accountState.selectedAccount?.defaultDashboardConfigurationId
        );
      }
    }, [
      selectedDashboardId,
      accountState.selectedAccount?.defaultDashboardConfigurationId,
    ]);

    useEffect(() => {
      fetchLayout();
    }, [fetchLayout, accountState.selectedAccount?.id]);

    return {
      configurations: dashboardLayouts.configurations,
      selectedConfigurationId:
        selectedDashboardId ??
        accountState.selectedAccount?.defaultDashboardConfigurationId,
      setSelectedConfiguration,
      selectedDashboard,
      renameDashboard,
      createDashboard,
      deleteDashboard,
      loading,
      error,
      clearError,
    };
  };
